import React from "react";
import { Box, Typography, Container, Divider, Grid, useTheme, useMediaQuery } from "@mui/material";
import VoterRegistration from "./../../assets/svgIcons/_x31_9_Voter_Registration.svg";
import ListDetails from "./../../assets/svgIcons/list-details.svg";
import FileText from "./../../assets/svgIcons/file-text.svg";
import FileCheck from "./../../assets/svgIcons/file-check.svg";
import CreditCard from "./../../assets/svgIcons/credit-card.svg";
import EligibilityCard, { StepData } from "../../components/ui/EligibilityCard";
import { PrimaryButton } from "../../components/ui/Button";

// Partner "pre-eligibility" flow's static success screen — shown instead of
// EligibilityConfirmation (see DynamicForm.tsx's eligibility-screen swap and
// its isPartnerPreEligibilityFlow-gated submit branch) when the applicant's
// eligibility was already established upstream by the partner, so no live
// BRE call/loading/error state is needed here — always the "eligible" UI.
interface Props {
  onNext: () => void;
  firstName: string;
}

const steps: StepData[] = [
  { icon: ListDetails, title: "Applicant & Loan Details" },
  { icon: FileText, title: "Provisional Loan Offer Letter" },
  { icon: FileText, title: "Additional Details & Documents" },
  { icon: FileCheck, title: "Final Verification & Sanction" },
  { icon: CreditCard, title: "Loan Disbursement" },
];

const StaticEligibilitySuccess: React.FC<Props> = ({ onNext, firstName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Container
      maxWidth={false}
      disableGutters
      className="eligibility-container"
      sx={{
        px: { xs: 0, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        mb={{ xs: 2, sm: 3 }}
      >
        <Box
          my={{ xs: 1, sm: 2 }}
          sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <img
            src={VoterRegistration}
            alt="eligible check icon"
            width={isMobile ? 80 : 117}
            height={isMobile ? 64 : 94}
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </Box>

        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: "#008688",
            fontSize: { xs: "20px", sm: "22px", md: "24px" },
            lineHeight: "120%",
            letterSpacing: "-0.5px",
            textAlign: "center",
            px: { xs: 1, sm: 0 },
            mt: { xs: 1, sm: 2 },
          }}
        >
          Congratulations,{" "}
          <Box component="span" fontWeight={700} color="#000">
            {firstName}!
          </Box>
        </Typography>

        <Typography
          variant="body2"
          className="eligibility-message-text"
          sx={{
            mb: { xs: 1, sm: 1 },
            maxWidth: 600,
            fontSize: { xs: "14px", sm: "15px !important" },
            color: "#7f7f7f",
            lineHeight: { xs: "1.4", sm: "1.5" },
            px: { xs: 1, sm: 0 },
          }}
        >
          You are eligible for a student loan! Based on the information
          you've provided, we've crafted a financing solution to power your
          academic journey. Take the next step toward your educational
          dreams — we'll be with you every step of the way.
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "flex-start" },
            my: { xs: 2 },
          }}
        >
          <Grid container spacing={2} justifyContent={{ xs: "center", sm: "flex-start" }}>
            <Grid item xs={12}>
              <PrimaryButton
                type="button"
                fullWidth
                onClick={onNext}
                sx={{
                  backgroundColor: "#ffd700 !important",
                  color: "#000000 !important",
                  fontSize: { xs: "14px", sm: "16px" },
                  fontWeight: "700 !important",
                  fontFamily: "Jost, sans-serif !important",
                  borderRadius: "8px !important",
                  padding: "12px 32px !important",
                  textTransform: "none !important",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1) !important",
                  "&:hover": {
                    backgroundColor: "#ffed4e !important",
                    boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.15) !important",
                  },
                }}
              >
                Continue for Provisional Offer
              </PrimaryButton>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Divider sx={{ my: { xs: 2, sm: 3 } }} />

      <Typography
        className="eligibility-sub-heading"
        mt={{ xs: 1, sm: 2 }}
        mb={{ xs: 1.5, sm: 2 }}
        fontWeight={600}
        fontSize={{ xs: "16px", sm: "18px" }}
        color="#333"
        sx={{ px: { xs: 1, sm: 0 } }}
      >
        Next Steps
      </Typography>

      <Box sx={{ mb: { xs: 2, sm: 3 }, px: { xs: 0, sm: 0 }, width: "100%" }}>
        <EligibilityCard steps={steps} />
      </Box>
    </Container>
  );
};

export default StaticEligibilitySuccess;
